import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {PearlHarborGameConfig} from "../PearlHarborGameConfig";
import {MathHelper} from "@nolimitcity/slot-game/bin/utils/MathHelper";
import {gsap, TimelineLite} from "gsap";
import {ITargetArea} from "../server/PearlHarborModel";
import {PearlHarbor} from "../PearlHarbor";
import {PearlHarborSoundManager} from "../sound/PearlHarborSoundManager";

export class ActiveSymbolBackground extends PIXI.Container implements StageLayer {
    private static readonly defaultAreaData:ITargetArea = {
        center : [3, 6],
        reelSize : 3,
        rowSize : 6,
        actualReelSize : 3,
        actualRowSize : 6,
        reelStart : 2,
        reelEnd : 4,
        rowStart : 4,
        rowEnd : 9
    };

    private _gradientSupported:boolean = true;
    private _symWidth:number;
    private _symHeight:number;
    private _totalWidth:number;
    private _totalHeight:number;
    private _offsetX:number;
    private _offsetY:number;

    private _shadowCanvas:HTMLCanvasElement;
    private _shadowTexture:PIXI.Texture;
    private _activeArea:ITargetArea;
    private _shadow:PIXI.Graphics;
    public crosshair:PIXI.Graphics;

    private calcHypotenuse(a:number, b:number):number {
        return MathHelper.roundToDecimals(Math.sqrt((a * a) + (b * b)), 2);
    }

    private createGradTexture(center:number[], activeRadius:number, rectWidth:number, rectHeight:number):PIXI.Texture {
        if(!this._shadowCanvas) {
            this._shadowCanvas = document.createElement('canvas');
            this._shadowCanvas.width = this._totalWidth;
            this._shadowCanvas.height = this._totalHeight;
        }
        if(this._shadowTexture) {
            this._shadowTexture.destroy(true);
        }
        const ctx = this._shadowCanvas.getContext('2d');
        ctx!.clearRect(0, 0, this._shadowCanvas.width, this._shadowCanvas.height);
        const centerX = center[0] - this._offsetX,
            centerY = center[1] - this._offsetY;
        try {
            const grd = ctx!.createRadialGradient(centerX, centerY, (rectWidth + rectHeight) * 0.5 * 0.5, centerX, centerY, Math.min(activeRadius + 100, this._totalWidth * 0.5));
            grd.addColorStop(0, 'rgb(91, 110, 129, 0.4)');
            grd.addColorStop(1, 'rgb(91, 110, 129, 0.9)');
            ctx!.fillStyle = grd;
        } catch {
            ctx!.fillStyle = "rgba(91, 110, 129, 0.7)";
            this._gradientSupported = false;
        }
        ctx!.fillRect(0, 0, this._shadowCanvas.width, this._shadowCanvas.height);
        this._shadowTexture = PIXI.Texture.from(this._shadowCanvas);
        return this._shadowTexture;
    }

    constructor() {
        super();
        this.name = "ActiveSymbolBackground";
        this.position.set(53, 161);
        this._activeArea = Object.assign({}, ActiveSymbolBackground.defaultAreaData);
        this._symWidth = PearlHarborGameConfig.symbolWidth;
        this._symHeight = PearlHarborGameConfig.symbolHeight;
        this._totalWidth = 7 * this._symWidth + 10;
        this._totalHeight = 7 * this._symHeight + 10;
        this._offsetX = -this._symWidth * 0.5 - 5;
        this._offsetY = -this._symHeight * 0.5 - 5;
        this._gradientSupported = true;

        this.crosshair = new PIXI.Graphics();
        this.crosshair.name = "circle";

        this._shadow = new PIXI.Graphics();
        this._shadow.name = "shadow";
        this._shadow.position.set(this._offsetX, this._offsetY);
    }

    private hasExpanded():boolean {
        if(this._activeArea.actualReelSize != ActiveSymbolBackground.defaultAreaData.reelSize) {
            return true;
        }

        if(this._activeArea.actualRowSize != ActiveSymbolBackground.defaultAreaData.rowSize) {
            return true;
        }
        return false;
    }

    private drawShapes(reset:boolean = false, withAnimation:boolean = false):TimelineLite {
        if(reset) {
            if(!this.hasExpanded()) {
                return gsap.timeline();
            }
            this._activeArea.reelStart = ActiveSymbolBackground.defaultAreaData.reelStart;
            this._activeArea.reelEnd = ActiveSymbolBackground.defaultAreaData.reelEnd;
            this._activeArea.rowStart = ActiveSymbolBackground.defaultAreaData.rowStart;
            this._activeArea.rowEnd = ActiveSymbolBackground.defaultAreaData.rowEnd;
            this._activeArea.reelSize = ActiveSymbolBackground.defaultAreaData.reelSize;
            this._activeArea.rowSize = ActiveSymbolBackground.defaultAreaData.rowSize;
        }

        const actualReelStart:number = Math.max(this._activeArea.reelStart, 0),
            actualReelEnd:number = Math.min(6, this._activeArea.reelEnd),
            actualRowStart:number = Math.max(this._activeArea.rowStart, 0),
            actualRowEnd:number = Math.min(13, this._activeArea.rowEnd),
            actualReelSize:number = actualReelEnd - actualReelStart + 1,
            actualRowSize:number = actualRowEnd - actualRowStart + 1,

            top:number = (actualRowStart - 1) * this._symHeight * 0.5,
            left:number = (actualReelStart - 0.5) * this._symWidth + 5,
            rectHeight:number = actualRowSize * this._symHeight * 0.5,
            rectWidth:number = actualReelSize * this._symWidth,
            center:number[] = [left + rectWidth * 0.5, top + rectHeight * 0.5],
            activeRadius = this.calcHypotenuse(rectWidth * 0.5, rectHeight * 0.5);

        //square and circle
        this._activeArea.actualReelSize = actualReelSize;
        this._activeArea.actualRowSize = actualRowSize;

        return new TimelineLite().add([
            () => {
                PearlHarborSoundManager.reelAreaChangeSound(reset, withAnimation);
                const scaleFactor = 3;

                this.crosshair.cacheAsBitmap = false;
                this.crosshair.clear();
                this.crosshair.position.set(center[0] + this.x, center[1] + this.y);
                this.crosshair.beginFill(0x000000, 0);
                // rect
                this.crosshair.lineStyle(6 * scaleFactor, 0x111111, 1, 0.5);
                this.crosshair.drawRect(-rectWidth * scaleFactor * 0.5, -rectHeight * scaleFactor * 0.5, rectWidth * scaleFactor, rectHeight * scaleFactor);
                // circles
                this.crosshair.lineStyle(8 * scaleFactor, 0x111111, 0.3, 1);
                this.crosshair.drawCircle(0, 0, activeRadius * scaleFactor + 80);
                this.crosshair.lineStyle(6 * scaleFactor, 0x111111, 1, 1);
                this.crosshair.drawCircle(0, 0, activeRadius * scaleFactor);
                // horizontal lines
                this.crosshair.lineStyle(6 * scaleFactor, 0x111111, 1, 0.5);
                this.crosshair.moveTo(-(activeRadius + 90) * scaleFactor, 0).lineTo(-rectWidth * scaleFactor * 0.5, 0);
                this.crosshair.moveTo(rectWidth * scaleFactor * 0.5, 0).lineTo((activeRadius + 90) * scaleFactor, 0);
                // vertical lines
                this.crosshair.moveTo(0, -(activeRadius + 90) * scaleFactor).lineTo(0, -rectHeight * scaleFactor * 0.5);
                this.crosshair.moveTo(0, rectHeight * scaleFactor * 0.5).lineTo(0, (activeRadius + 90) * scaleFactor);
                this.crosshair.endFill();
                this.crosshair.cacheAsBitmap = true;
                this.crosshair.scale.set(1 / scaleFactor);

                this._shadow.clear();
                this._shadow.beginTextureFill({texture : this.createGradTexture(center, activeRadius, rectWidth, rectHeight)});
                this._shadow.drawRect(0, 0, this._totalWidth, this._totalHeight);
                this._shadow.beginHole();
                this._shadow.drawRect(left - this._offsetX, top - this._offsetY, rectWidth, rectHeight);
                this._shadow.endHole();
            }
        ]);
    }

    public onGameReady():void {
        this.drawShapes();
        this.addChild(this._shadow);
    }

    private hasChange(border:number[]):boolean {
        if(border[0] != this._activeArea.rowStart) {
            return true;
        }
        if(border[1] != this._activeArea.rowEnd) {
            return true;
        }
        if(border[2] != this._activeArea.reelStart) {
            return true;
        }
        if(border[3] != this._activeArea.reelEnd) {
            return true;
        }
        return false;
    }

    //up, down, left, right
    public updateActiveArea(border:number[]):TimelineLite {
        if(this.hasChange(border)) {
            this._activeArea.rowStart = Math.min(this._activeArea.rowStart, border[0]);
            this._activeArea.rowEnd = Math.max(this._activeArea.rowEnd, border[1]);
            this._activeArea.reelStart = Math.min(this._activeArea.reelStart, border[2]);
            this._activeArea.reelEnd = Math.max(this._activeArea.reelEnd, border[3]);
            this._activeArea.actualReelSize = (Math.min(6, this._activeArea.reelEnd) - Math.max(this._activeArea.reelStart, 0)) + 1;
            this._activeArea.actualRowSize = (Math.min(13, this._activeArea.rowEnd) - Math.max(this._activeArea.rowStart, 0)) + 1;
            return new TimelineLite().add(this.drawShapes(false, true)).add(()=>{
                for(let i:number = 0; i < 7; i++) {
                    PearlHarbor.allReelsView.mainReelModels[i].reelView.setSymbolActiveState();
                }
            });
        }
        return new TimelineLite();
    }

    public reset():void {
        this.drawShapes(true);
    }

    public hideOnFSIntro():void {
        NolimitApplication.getLayerByName(PearlHarborGameConfig.LAYERS.ACTIVE_AREA)!.visible = false;
        this.visible = false;
    }

    public showOnFSOutro():void {
        NolimitApplication.getLayerByName(PearlHarborGameConfig.LAYERS.ACTIVE_AREA)!.visible = true;
        this.visible = true;
    }

    public get targetArea():ITargetArea {
        return this._activeArea;
    }
}